import { Button } from '@monksflow/monks-ui';
import { PencilIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  PageHeader,
  PageHeaderActions,
  PageHeaderBreadcrumb,
  PageHeaderBreadcrumbLink,
  PageHeaderBreadcrumbPage,
  PageHeaderIdentity,
} from '@/shared/ui/layout';
import { usePermissions } from '@/entities/user';
import { ROUTES } from '@/shared/config/routes';
import { DisabledReasonTooltip } from '@/shared/ui/DisabledReasonTooltip';
import type { AudienceHeaderV2Props } from './AudienceHeaderV2.types';

/**
 * Redesigned audience-detail header. The trail names where back goes instead of
 * repeating the persona, and the coloured section badges of the V1 header are
 * gone: the prototype's Knowledge pages carry a breadcrumb alone.
 */
export const AudienceHeaderV2 = ({
  title,
  persona,
  action,
  onEdit,
  onStartConversation,
}: AudienceHeaderV2Props) => {
  const { t } = useTranslation('audiences');
  const { canManageAudiences } = usePermissions();

  // Nothing is settled to act on until generation finishes.
  const isReady = persona?.creationStatus === 'ready';
  const showActions = Boolean(persona) && isReady;
  const isDefault = persona?.type === 'Default';

  return (
    <PageHeader>
      <PageHeaderIdentity>
        <PageHeaderBreadcrumb>
          <PageHeaderBreadcrumbLink href={ROUTES.KNOWLEDGE_HUB}>
            {t('audienceDetail.audienceHeaderV2.knowledge')}
          </PageHeaderBreadcrumbLink>
          <PageHeaderBreadcrumbLink href={ROUTES.KNOWLEDGE_HUB_AUDIENCES}>
            {t('audienceDetail.audienceHeaderV2.audiences')}
          </PageHeaderBreadcrumbLink>
          <PageHeaderBreadcrumbPage>{title}</PageHeaderBreadcrumbPage>
        </PageHeaderBreadcrumb>
      </PageHeaderIdentity>

      {showActions && (
        <PageHeaderActions>
          {action}
          {canManageAudiences &&
            onEdit &&
            (isDefault ? (
              <DisabledReasonTooltip
                reason={t('audienceDetail.audienceHeaderV2.defaultCannotBeEdited')}
              >
                <Button variant="outline" disabled>
                  <PencilIcon className="size-4" />
                  {t('audienceDetail.audienceHeaderV2.edit')}
                </Button>
              </DisabledReasonTooltip>
            ) : (
              <Button variant="outline" onClick={onEdit}>
                <PencilIcon className="size-4" />
                {t('audienceDetail.audienceHeaderV2.edit')}
              </Button>
            ))}
          {onStartConversation && (
            <Button onClick={onStartConversation}>
              {t('audienceDetail.audienceHeaderV2.startConversation')}
            </Button>
          )}
        </PageHeaderActions>
      )}
    </PageHeader>
  );
};

AudienceHeaderV2.displayName = 'AudienceHeaderV2';
